/** Debounce viewport changes and serialize writes, retaining only the latest state. */
export function createViewportSaveQueue<T extends { timestamp: number }>(
    save: (state: T) => void | Promise<void>,
    onError: (error: unknown) => void,
    delay = 600,
) {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let pending: { state: T; signature: string } | undefined;
    let active: string | undefined;
    let saved: string | undefined;
    let disposed = false;

    const flush = async () => {
        if (disposed || active || !pending) return;
        const next = pending;
        pending = undefined;
        active = next.signature;
        try { await save(next.state); saved = next.signature; }
        catch (error) { onError(error); }
        finally {
            active = undefined;
            if (!timer && pending && !disposed) void flush();
        }
    };

    return {
        schedule(state: T) {
            if (disposed) return;
            // Wall-clock timestamps and callback identity do not represent edits.
            const signature = JSON.stringify({ ...state, timestamp: 0 });
            if (signature === pending?.signature) return;
            if (timer) { clearTimeout(timer); timer = undefined; }
            pending = signature === active || (!active && signature === saved) ? undefined : { state, signature };
            if (!pending) return;
            timer = setTimeout(() => { timer = undefined; void flush(); }, delay);
        },
        dispose() {
            disposed = true;
            if (timer) clearTimeout(timer);
            pending = undefined;
        },
    };
}
